import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FileText, ShieldCheck, X, Info, ArrowUpRight } from 'lucide-react';
import type { PolicyDocument } from '@/lib/auth/policies';

interface PolicyDialogProps {
  type: PolicyDocument['document_type'] | null;
  documents: PolicyDocument[];
  error?: string;
  onClose: () => void;
}

export default function PolicyDialog({ type, documents, error, onClose }: PolicyDialogProps) {
  const dialog = useRef<HTMLDialogElement>(null);
  const heading = useId();
  const description = useId();
  const open = type !== null;
  const document = documents.find((item) => item.document_type === type);
  const terms = type === 'terms_of_service';
  const Icon = terms ? FileText : ShieldCheck;
  const title = terms ? 'Terms of Service' : 'Privacy Policy';

  useEffect(() => {
    const element = dialog.current;
    if (!open || !element) return;
    // Native modal dialogs contain keyboard focus and restore it to the opener on close.
    const previousOverflow = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    element.showModal();
    return () => {
      element.close();
      window.document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  return createPortal(<dialog ref={dialog} aria-labelledby={heading} aria-describedby={description}
    className="auth-policy-dialog m-auto w-[calc(100%_-_2rem)] max-w-2xl overflow-hidden rounded-xl border border-background-200 bg-background-50 p-0 text-foreground-950 shadow-xl"
    onCancel={(event) => { event.preventDefault(); onClose(); }}
    onClick={(event) => {
      if (event.target !== event.currentTarget) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) onClose();
    }}>
    <div className="flex max-h-[85dvh] flex-col">
      <header className="flex shrink-0 items-start gap-3 border-b border-background-200 px-5 py-4 sm:px-6">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700"><Icon size={22} aria-hidden="true" /></div>
        <div className="min-w-0 flex-1">
          <h2 id={heading} tabIndex={-1} autoFocus className="font-heading text-lg font-semibold outline-none">{title}</h2>
          <p id={description} className="mt-1 text-xs text-foreground-700">Review this document before accepting it during signup.</p>
          {document && <p className="mt-2 text-[11px] text-foreground-700">Version {document.version} · {document.locale.toUpperCase()}</p>}
        </div>
        <button type="button" onClick={onClose} aria-label="Close policy"
          className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"><X size={20} /></button>
      </header>
      <section className="min-h-0 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6" aria-label="Policy content">
        {document ? <>
          {document.is_development && <div role="note" className="mb-5 flex items-start gap-2 rounded-lg border border-background-200 bg-background-100 p-3 text-xs leading-5 text-foreground-800">
            <Info size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
            <p><strong className="font-semibold">Development draft</strong><br />For testing only. Not approved for production.</p>
          </div>}
          <div className="flex flex-col gap-4 text-sm leading-7 text-foreground-800">
            {document.content.split(/\n\s*\n/).map((paragraph, index) => <p key={index} className="whitespace-pre-wrap break-words">{paragraph}</p>)}
          </div>
        </> : <p role={error ? 'alert' : 'status'} className="py-6 text-sm text-foreground-700">{error || 'Loading policy...'}</p>}
      </section>
      <footer className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-background-200 bg-background-100 px-5 py-3 sm:px-6">
        {document && <a href={`/${terms ? 'terms' : 'privacy'}?document=${document.id}`} target="_blank" rel="noopener noreferrer"
          className="inline-flex min-h-11 items-center gap-1 text-xs font-medium text-primary-700 underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-primary-500">Open full page<ArrowUpRight size={14} aria-hidden="true" /></a>}
        <button type="button" onClick={onClose} className="ml-auto min-h-11 cursor-pointer rounded-lg bg-primary-600 px-5 text-sm font-semibold text-background-50 hover:bg-primary-700 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2">Done reading</button>
      </footer>
    </div>
  </dialog>, window.document.body);
}
